// StableSurfaces Dependent references (docs/stable-surfaces/model.md): precedence over a step's or tool's Surface and
// control reference slots. No runtime imports (browser graph).
import type { AppTool } from '../app-tools/schema.ts'
import type { JourneyAction } from '../journey/steps.ts'
import type { DefinitionId, SurfaceId } from './id.ts'
import type { ComponentTarget } from './target.ts'

/** A slot's id; `''` is absent (Resolution: it denotes no entity). */
function present<T extends SurfaceId | DefinitionId | null | undefined>(slot: T): T | undefined {
  return slot === '' ? undefined : slot
}

/** A step's Surface: its target's `surfaceId`, else its flat `surfaceId`. An identity, not the Surfaces an operation visits. */
export function stepSurface(action: { readonly component?: { readonly surfaceId?: SurfaceId }; readonly surfaceId?: SurfaceId } | undefined): SurfaceId | undefined {
  return present(action?.component?.surfaceId) ?? present(action?.surfaceId)
}

/** A step's control: its target's `controlId`, else its flat `controlId`. */
export function stepControl(step: { readonly component?: { readonly controlId?: DefinitionId }; readonly controlId?: DefinitionId } | undefined): DefinitionId | undefined {
  return present(step?.component?.controlId) ?? present(step?.controlId)
}

/** A flat control reference as the flat target form; undefined unless both slots are present. */
export function flatComponentTarget(ref: { readonly surfaceId?: SurfaceId; readonly controlId?: DefinitionId }): ComponentTarget | undefined {
  const surfaceId = present(ref.surfaceId), controlId = present(ref.controlId)
  return surfaceId && controlId ? { surfaceId, controlId } : undefined
}

/** The Surface a tool operates on: none for a reach tool; else its `operatingSurfaceId`, else its base revision's first action's Surface.
 *  Read from explicit metadata or original steps, never from learned checks. */
export function operatingSurface(tool: AppTool): SurfaceId | undefined {
  if (present(tool.reachSurfaceId)) return undefined
  return present(tool.operatingSurfaceId) ?? stepSurface(tool.revisions[0]?.steps.actions[0])
}

/** The operating Surface a legacy tool's own recorded steps establish: the first action of its base revision that names a
 *  Surface, for a tool without explicit anchor. Authored evidence, never a learned check (evals/journey-outcomes/index.ts). */
export function recordedOperatingSurface(tool: AppTool): SurfaceId | undefined {
  if (present(tool.reachSurfaceId) || present(tool.operatingSurfaceId)) return undefined
  return tool.revisions[0]?.steps.actions.map(action => stepSurface(action)).find(surface => surface !== undefined)
}

/** The Surface whose session a reach boundary step boots from. null: a declared project start; undefined: no reach boundary. */
export function reachStart(step: Pick<JourneyAction, 'appTool' | 'reachSurface'> | undefined): SurfaceId | null | undefined {
  const tool = step?.appTool, reach = step?.reachSurface
  if (tool?.startingSurfaceId === null) return null
  const toolStart = present(tool?.startingSurfaceId) ?? present(tool?.reachSurfaceId)
  if (toolStart !== undefined) return toolStart
  if (reach?.startSurfaceId === null) return null
  return present(reach?.startSurfaceId) ?? present(reach?.surfaceId)
}

/** The Surface the step's reach plan ends on: a `reachSurface` step's own id, else the reach stamp's, else the reach tool's. */
export function reachDestination(step: Pick<JourneyAction, 'type' | 'surfaceId' | 'reachSurface' | 'appTool'> | undefined): SurfaceId | undefined {
  return (step?.type === 'reachSurface' ? present(step.surfaceId) : undefined)
    ?? present(step?.reachSurface?.surfaceId) ?? present(step?.appTool?.reachSurfaceId)
}

/** The Surface a context is on after calling `tool`: the reach tool's destination, else the call's Surface, else the tool's operating Surface. */
export function surfaceAfterCall(tool: AppTool | undefined, call: { readonly surfaceId?: SurfaceId }): SurfaceId | undefined {
  return present(tool?.reachSurfaceId) ?? present(call.surfaceId) ?? (tool && operatingSurface(tool))
}
